/**
 * SW2-2394 — assembling the Usersnap custom-data payload so it survives the
 * Slack post's truncation.
 *
 * Separate from the component because it is pure: no React, no contexts, and so
 * testable on its own. The component supplies the scalars and the timeline.
 */

/**
 * Character budget for the custom-data block.
 *
 * The Slack post truncates, and the limit applies to the whole message rather
 * than to this block alone: reports carrying a one-line title and comment
 * arrived complete, where a comparable payload under a long title and a
 * sentence of comment was cut.
 *
 * So the reporter's own words and the evidence share one budget, and no fixed
 * number here can guarantee a fit. This is set well under the observed limit to
 * leave room for a talkative reporter, which is the normal case — a real report
 * is a paragraph, not "TEST".
 *
 * Overflowing is survivable rather than silent: `timeline` sorts last and is
 * ordered newest-first, so what Slack cuts is the oldest events, and the
 * trim marker is placed first so it is never the thing that goes missing.
 */
const PAYLOAD_BUDGET = 400;

/** Usersnap pretty-prints custom data at four spaces; measure what it will send. */
const JSON_INDENT = 4;

/**
 * The payload with as much of the timeline as fits, newest first.
 *
 * Drops the oldest entries one at a time and records how many went, so a reader
 * can tell a quiet session from a trimmed one. The scalar context is never
 * sacrificed — it is bounded and small, while the timeline is neither.
 */
export function fitToBudget(
  scalars: Record<string, any>,
  timeline: string[],
): Record<string, any> {
  const size = (value: Record<string, any>) =>
    JSON.stringify(value, null, JSON_INDENT).length;

  let kept = timeline.length;

  while (kept >= 0) {
    const omitted = timeline.length - kept;
    const shown = timeline.slice(0, kept);

    // The marker goes first, not last. Slack truncates the tail, so a marker
    // appended to the end is the first line cut — and losing it is worse than
    // losing an event, because the reader then has no way to tell a trimmed
    // timeline from a quiet session.
    if (omitted > 0) {
      shown.unshift(`(${omitted} older omitted)`);
    }

    const candidate = { ...scalars, timeline: shown };

    if (kept === 0 || size(candidate) <= PAYLOAD_BUDGET) {
      return candidate;
    }

    kept -= 1;
  }

  // Unreachable: the loop returns at kept === 0. Here so every path is typed.
  return { ...scalars, timeline: [] };
}

